<template>
  <div class="message" :class="messageTypeClass">
    <div class="message-content" @click="handleMessageClick">
      <div class="message-content-inner">
        <slot>{{ message }}</slot>
      </div>
    </div>
    <button v-if="isHideable" class="hide-button" @click="$emit('hide')">
      ×
    </button>
  </div>
</template>

<script>
export default {
  name: 'ImportantMessage',
  props: {
    type: {
      type: String,
      default: 'info',
      validator: value => ['info', 'notice', 'warning', 'success', 'error'].includes(value),
    },
    message: {
      type: String,
      default: '',
    },
    isHideable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    messageTypeClass() {
      switch (this.type) {
        case 'info':
        case 'success':
          return 'message-info';
        case 'notice':
          return 'message-notice';
        case 'warning':
        case 'error':
          return 'message-warning';
        default:
          return 'message-info';
      }
    },
  },
  methods: {
    handleMessageClick(event) {
      // Check if the clicked element is a link
      let { target } = event;

      // If it's not the link itself, check if it's within a link
      if (target.tagName !== 'A') {
        target = target.closest('a');
      }

      // If we didn't find a link, do nothing
      if (!target) {
        return;
      }

      // If it's a link with data-action attribute, handle it with the global action handler
      if (target.hasAttribute('data-action')) {
        event.preventDefault();
        const action = target.getAttribute('data-action');

        // Just simulate a direct click on the document to trigger the global handler
        const actionEl = document.createElement('button');
        actionEl.setAttribute('data-action', action);
        actionEl.style.display = 'none';
        document.body.appendChild(actionEl);
        actionEl.click();
        document.body.removeChild(actionEl);
        return;
      }

      // Check if it's an internal link by examining the href attribute
      const href = target.getAttribute('href');

      // If it's an internal link (starts with '/' and doesn't have 'http://', 'https://', etc.)
      if (href && href.startsWith('/') && !href.match(/^(https?:|mailto:|tel:|ftp:|#)/)) {
        event.preventDefault();

        // Use Vue Router for navigation instead of browser default behavior
        this.$router.push(href);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.message {
  position: relative;
  padding: 8px 16px;
  margin-bottom: 10px;
  border-radius: 8px;
  display: flex;
  align-items: center;

  &-content {
    flex: 1;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 20px;

    &-inner {
      width: 100%;
      font-size: 14px;
      line-height: 1.4;
    }

    :deep(a) {
      text-decoration: underline;
      cursor: pointer;
    }

    :deep(p) {
      margin: 0;
    }
  }

  &-info {
    background-color: var(--success-bg, #e6f7ef);
    color: var(--success-text, #1d7d5c);
    border: 1px solid var(--success-border, #a9e2cd);

    :deep(a) {
      color: var(--success-text, #1d7d5c);
    }
  }

  &-notice {
    background-color: var(--warning-bg, #fff8e6);
    color: var(--warning-text, #b86e00);
    border: 1px solid var(--warning-border, #ffd685);

    :deep(a) {
      color: var(--warning-text, #b86e00);
    }
  }

  &-warning {
    background-color: var(--error-bg, #feecec);
    color: var(--error-text, #cd3a3a);
    border: 1px solid var(--error-border, #f6baba);

    :deep(a) {
      color: var(--error-text, #cd3a3a);
    }
  }
}

.hide-button {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  padding: 0 5px;
  line-height: 1;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}
</style>
